// packages/api/src/hooks/utils/useSocketRoom.ts
//
// Phase 12 analista-queue-kpis-notif Plan 11 (REQ-10).
// Hook utilitário: joina uma room ao mount; sai ao unmount.
//
// Contract: docs/contracts/ESCR-analista-queue-kpis-notif-05.md.
//
// **Uso típico Plan 12 (war-room realtime):**
// ```tsx
// useSocketRoom('war-room');
// useSocketEvent('proposal.status_changed', () => {
//   queryClient.invalidateQueries({ queryKey: queryKeys.warRoom.snapshot() });
// });
// ```
//
// **Uso para proposal detail (analista vê live updates do próprio aggregate):**
// ```tsx
// useSocketRoom(`proposal:${proposalId}`);
// ```
//
// Backend Phase 09 D-58 verifica JWT no handshake + permissions check para
// `analyst:` / `war-room` rooms (server-side gating). Client emit 'join' é
// informativo — o server ignora se sem permissão.

import { useEffect } from 'react';

import { acquireSharedSocket, releaseSharedSocket } from './socket-shared';

/**
 * Joina `roomName` ao mount; emite `leave` ao unmount.
 *
 * No-op quando:
 *   - `roomName` é undefined/null/empty
 *   - usuário não está autenticado (sem access token)
 *
 * Re-join automático quando `roomName` muda (re-execução do useEffect).
 */
export function useSocketRoom(roomName: string | null | undefined): void {
  useEffect(() => {
    if (!roomName) return;

    const socket = acquireSharedSocket();
    if (!socket) return;

    const join = () => {
      socket.emit('join', { room: roomName });
    };

    if (socket.connected) {
      join();
    } else {
      socket.once('connect', join);
    }

    return () => {
      try {
        if (socket.connected) {
          socket.emit('leave', { room: roomName });
        }
        socket.off('connect', join);
      } catch {
        // ignore — best-effort cleanup
      }
      releaseSharedSocket();
    };
  }, [roomName]);
}
